import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  formatFilterDate,
  isEmptyFilterValue,
  toDatepickerValue,
} from 'src/app/pages/operation/shared/operation-filter.utils';

import { PositionCustodyFilter } from '../interfaces/position-custody-filter';
import { hasQueryableFilters } from '../shared/position-consult.utils';
import { PositionConsultListService } from '../shared/position-consult-list.service';

@Component({
  standalone: false,
  selector: 'app-position-consult-filter',
  templateUrl: './position-consult-filter.component.html',
  styleUrls: ['./position-consult-filter.component.scss'],
})
export class PositionConsultFilterComponent implements OnInit, OnChanges {
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();

  @Input() get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.isOpenChange.emit(this._isOpen);
  }

  formFilter: FormGroup = new FormGroup({});
  datepickerResetKey = 0;

  private _isOpen = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly positionConsultListService: PositionConsultListService,
  ) {}

  ngOnInit(): void {
    this.setForm();
    this.loadFormFromFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.loadFormFromFilters();
    }
  }

  close(): void {
    this.closed.emit();
  }

  cleanForm(): void {
    this.formFilter.reset({
      positionDate: null,
      financialInstrumentCode: '',
      projectId: '',
      holderAccount: '',
    });
    this.refreshDatepickerViews();
    this.positionConsultListService.setFilters({});
  }

  filter(): void {
    const filters = this.buildFiltersFromForm();

    // Qualquer campo basta — data NÃO é obrigatória.
    if (!hasQueryableFilters(filters)) {
      return;
    }

    this.positionConsultListService.setFilters(filters);
    this.close();
  }

  onPositionDateChange(event: Event): void {
    const iso = (event as CustomEvent<{ value?: string }>).detail?.value?.trim() ?? '';

    if (!iso) {
      this.formFilter.get('positionDate')?.setValue(null);
      return;
    }

    const date = toDatepickerValue(iso);
    if (date) {
      this.formFilter.get('positionDate')?.setValue(date);
    }
  }

  trackDatepickerResetKey(_index: number, key: number): number {
    return key;
  }

  private setForm(): void {
    // Sem Validators.required — todos os campos são opcionais.
    this.formFilter = this.formBuilder.group({
      positionDate: [null],
      financialInstrumentCode: [''],
      projectId: [''],
      holderAccount: [''],
    });
  }

  private loadFormFromFilters(): void {
    const filters = this.positionConsultListService.filters.getValue();

    this.formFilter.setValue({
      positionDate: isEmptyFilterValue(filters.positionDate)
        ? null
        : toDatepickerValue(filters.positionDate),
      financialInstrumentCode: filters.carbonCreditCode ?? '',
      projectId: filters.projectId ?? '',
      holderAccount: filters.accountCode ?? '',
    });
  }

  private refreshDatepickerViews(): void {
    this.datepickerResetKey++;
  }

  private buildFiltersFromForm(): PositionCustodyFilter {
    const formValues = this.formFilter.getRawValue();

    // Label UI = "Instrumento Financeiro"; valor CARB… → param BE carbonCreditCode.
    // Sem mask="instrument": a máscara tipicamente espera formato numérico e corrompe o CARB.
    const carbonCreditCode = formValues.financialInstrumentCode?.trim()?.toUpperCase() || undefined;

    return {
      positionDate: formatFilterDate(formValues.positionDate) || undefined,
      carbonCreditCode,
      projectId: formValues.projectId?.trim() || undefined,
      accountCode: formValues.holderAccount?.trim() || undefined,
    };
  }
}
